import {
  type PaginationState,
  type SortingState,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import React, { useCallback, useEffect, useState } from 'react';

import { useApiClient } from '@eluve/api-client-provider';
import { GraphileJobItem } from '@eluve/api-contract';
import {
  Button,
  ColDefBuilder,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Icon,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  ServerDataTable,
  Stack,
  Text,
  useToast,
} from '@eluve/components';
import { useDialog } from '@eluve/utility-hooks';

const AUTO_REFRESH_INTERVAL_MS = 30_000;
const ALL_STATUSES = 'ALL';

const statusLabels: Record<string, string> = {
  available: 'Available',
  locked: 'Locked',
  failed: 'Failed',
  scheduled: 'Scheduled',
};

type JobAction = 'fail' | 'complete' | 'reschedule';

export const GraphileJobsTab: React.FC = () => {
  const api = useApiClient();
  const toast = useToast();
  const [data, setData] = useState<GraphileJobItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState(ALL_STATUSES);

  const [sorting, setSorting] = useState<SortingState>([
    { id: 'createdAt', desc: true },
  ]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 50,
  });

  // Single dialog + selected job state (lifted from per-row)
  const dialog = useDialog();
  const [selectedJob, setSelectedJob] = useState<GraphileJobItem | null>(null);
  const [selectedAction, setSelectedAction] = useState<JobAction>('fail');
  const [processing, setProcessing] = useState(false);
  const [failReason, setFailReason] = useState('');

  const openAction = (job: GraphileJobItem, action: JobAction) => {
    setSelectedJob(job);
    setSelectedAction(action);
    setFailReason('');
    dialog.openDialog();
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const sort = sorting[0];
      const result = await api.adminOnCall.getGraphileJobs({
        query: {
          page: pagination.pageIndex + 1,
          pageSize: pagination.pageSize,
          sortBy: sort?.id,
          sortDirection: sort?.desc ? 'desc' : 'asc',
          status:
            statusFilter === ALL_STATUSES
              ? undefined
              : (statusFilter as
                  | 'available'
                  | 'locked'
                  | 'failed'
                  | 'scheduled'),
        },
      });
      if (result.status === 200) {
        setData(result.body.items);
        setTotalCount(result.body.totalCount);
      } else {
        toast.error('Failed to load graphile jobs.');
      }
    } finally {
      setLoading(false);
    }
  }, [api, pagination.pageIndex, pagination.pageSize, sorting, statusFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const interval = setInterval(fetchData, AUTO_REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleMutation = async () => {
    if (!selectedJob) return;
    if (selectedAction === 'fail' && !failReason.trim()) return;

    setProcessing(true);
    try {
      let result: { status: number; body: unknown };

      if (selectedAction === 'fail') {
        result = await api.adminOnCall.permanentlyFailGraphileJob({
          params: { jobId: selectedJob.id },
          body: { reason: failReason },
        });
      } else if (selectedAction === 'complete') {
        result = await api.adminOnCall.completeGraphileJob({
          params: { jobId: selectedJob.id },
          body: {},
        });
      } else {
        result = await api.adminOnCall.rescheduleGraphileJob({
          params: { jobId: selectedJob.id },
          body: { attempts: 0 },
        });
      }

      if (result.status === 200) {
        const labels: Record<JobAction, string> = {
          fail: 'permanently failed',
          complete: 'completed (removed)',
          reschedule: 'rescheduled',
        };
        toast.success(`Job ${selectedJob.id} ${labels[selectedAction]}`);
        fetchData();
      } else {
        toast.error(
          result.status === 404
            ? 'Job not found'
            : (result.body as { message: string }).message,
        );
      }
    } finally {
      setProcessing(false);
      dialog.closeDialog();
    }
  };

  const columns = React.useMemo(
    () =>
      new ColDefBuilder<GraphileJobItem>()
        .defaultSortable('id', 'Job ID')
        .defaultSortable('taskIdentifier', 'Task')
        .colDef({
          id: 'taskId',
          header: 'Task ID',
          accessorKey: 'taskId',
          enableSorting: true,
        })
        .colDef({
          id: 'jobQueueId',
          header: 'Queue ID',
          accessorKey: 'jobQueueId',
          cell: ({ row }) => {
            const val = row.original.jobQueueId;
            return val != null ? <Text size="sm">{val}</Text> : null;
          },
        })
        .colDef({
          id: 'status',
          header: 'Status',
          accessorKey: 'status',
          cell: ({ row }) => {
            const s = row.original.status;
            return <Text size="sm">{statusLabels[s] ?? s}</Text>;
          },
        })
        .colDef({
          id: 'attempts',
          header: 'Attempts',
          accessorKey: 'attempts',
          cell: ({ row }) => (
            <Text size="sm">
              {row.original.attempts} / {row.original.maxAttempts}
            </Text>
          ),
          enableSorting: true,
        })
        .dateSortable('createdAt', 'Created')
        .dateSortable('runAt', 'Run At')
        .colDef({
          id: 'lastError',
          header: 'Last Error',
          accessorKey: 'lastError',
          cell: ({ row }) => {
            const error = row.original.lastError;
            if (!error) return null;
            return (
              <span className="max-w-[200px] truncate text-sm" title={error}>
                {error}
              </span>
            );
          },
        })
        .colDef({
          id: 'lockedBy',
          header: 'Locked By',
          accessorKey: 'lockedBy',
          cell: ({ row }) => {
            const val = row.original.lockedBy;
            return val ? (
              <Text size="sm" className="font-mono">
                {val}
              </Text>
            ) : null;
          },
        })
        .colDef({
          id: 'payload',
          header: 'Payload',
          accessorKey: 'payload',
          cell: ({ row }) => {
            const payload = row.original.payload;
            if (payload == null) return null;
            const text = JSON.stringify(payload);
            return (
              <span
                className="max-w-[300px] truncate font-mono text-sm"
                title={text}
              >
                {text}
              </span>
            );
          },
        })
        .colDef({
          id: 'actions',
          header: 'Actions',
          cell: ({ row }) => {
            const job = row.original;
            const isLocked = job.status === 'locked';
            const isExhausted = job.attempts >= job.maxAttempts;
            return (
              <Stack direction="horizontal" gap={2}>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openAction(job, 'fail')}
                  disabled={isLocked}
                >
                  Fail
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openAction(job, 'complete')}
                  disabled={isLocked || !isExhausted}
                >
                  Complete
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openAction(job, 'reschedule')}
                  disabled={isLocked || !isExhausted}
                >
                  Reschedule
                </Button>
              </Stack>
            );
          },
        })
        .build(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualSorting: true,
    pageCount: Math.ceil(totalCount / pagination.pageSize),
    state: { pagination, sorting },
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
  });

  const actionLabels: Record<JobAction, string> = {
    fail: 'Permanently Fail',
    complete: 'Complete (Remove)',
    reschedule: 'Reschedule',
  };

  return (
    <Stack gap={2}>
      <Stack direction="horizontal" gap={2}>
        <Text size="sm" color="secondary">
          {loading
            ? 'Loading...'
            : `${totalCount} job${totalCount !== 1 ? 's' : ''} total`}
        </Text>
        <Text size="sm" color="secondary">
          Auto-refreshes every 30s
        </Text>
        <Select
          value={statusFilter}
          onValueChange={(v) => {
            setStatusFilter(v);
            setPagination((p) => ({ ...p, pageIndex: 0 }));
          }}
        >
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_STATUSES}>All Statuses</SelectItem>
            <SelectItem value="available">Available</SelectItem>
            <SelectItem value="locked">Locked</SelectItem>
            <SelectItem value="failed">Failed</SelectItem>
            <SelectItem value="scheduled">Scheduled</SelectItem>
          </SelectContent>
        </Select>
        <Button
          variant="outline"
          size="sm"
          onClick={fetchData}
          disabled={loading}
        >
          <Icon name="RefreshCw" size="auto" />
          Refresh
        </Button>
      </Stack>
      <Stack className="rounded-md border">
        <ServerDataTable<GraphileJobItem>
          table={table}
          isPending={loading}
          placeholder="No graphile worker jobs found."
        />
      </Stack>

      {selectedJob && (
        <Dialog
          open={dialog.isDialogOpen}
          onOpenChange={dialog.setIsDialogOpen}
        >
          <DialogContent
            showCloseButton={false}
            onInteractOutside={(e) => e.preventDefault()}
          >
            <DialogHeader>
              <DialogTitle>
                {actionLabels[selectedAction]} Job #{selectedJob.id}?
              </DialogTitle>
              <DialogDescription>
                Task: {selectedJob.taskIdentifier}
                <br />
                Attempts: {selectedJob.attempts} / {selectedJob.maxAttempts}
                {selectedAction === 'fail' && selectedJob.lastError && (
                  <>
                    <br />
                    <br />
                    Last Error: {selectedJob.lastError.slice(0, 200)}
                  </>
                )}
                <br />
                <br />
                {selectedAction === 'fail' &&
                  'This will prevent the job from retrying. A tag will be added indicating human intervention.'}
                {selectedAction === 'complete' &&
                  'This will permanently delete this job from the queue.'}
                {selectedAction === 'reschedule' &&
                  'This will reset attempts to 0 and allow the job to run again.'}
              </DialogDescription>
            </DialogHeader>
            {selectedAction === 'fail' && (
              <textarea
                className="w-full rounded-sm border p-2 text-sm"
                placeholder="Reason (required)"
                value={failReason}
                onChange={(e) => setFailReason(e.target.value)}
                rows={2}
              />
            )}
            <DialogFooter>
              <Button
                disabled={processing}
                onClick={dialog.closeDialog}
                variant="outline"
              >
                Cancel
              </Button>
              <Button
                disabled={
                  processing ||
                  (selectedAction === 'fail' && !failReason.trim())
                }
                onClick={handleMutation}
              >
                {processing ? 'Processing...' : actionLabels[selectedAction]}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </Stack>
  );
};
